import type { TrackingDriver } from '@/tracking/contracts/TrackingDriver'
import type { AxiosInstance, AxiosResponse } from 'axios'
import type { TrackingEvent, TrackingEventPayload } from '@/tracking/types/TrackingEvent.ts'
import { Capacitor } from '@capacitor/core'
import { useSessionStore } from '@/stores/useSessionStore'
import { useTrackingStore } from '@/stores/useTrackingStore'

type QueueEntry<K extends TrackingEvent = TrackingEvent> = {
  event: K
  data: TrackingEventPayload[K]
}

export class ApiTrackingDriver implements TrackingDriver {
  private readonly http: AxiosInstance
  private readonly queue: QueueEntry[] = []

  private hasStarted: boolean = false
  private shouldTrack: boolean = false

  private readonly _readyPromise: Promise<this>
  private _resolveReady!: (driver: this) => void
  private _rejectReady!: (err: unknown) => void

  constructor(http: AxiosInstance) {
    this.http = http
    this._readyPromise = new Promise((resolve, reject) => {
      this._resolveReady = resolve
      this._rejectReady = reject
    })
  }

  async start(): Promise<void> {
    try {
      const response = await this.http.post('tracking/session')

      this.setStoreFromResponse(response)

      this.shouldTrack = response.data.data.track || false

      this.hasStarted = true

      await this.sendQueue()
      this._resolveReady(this)
    } catch (error) {
      console.debug(error)

      this._rejectReady(error)
    }
  }

  ready(): Promise<this> {
    return this._readyPromise
  }

  async send<T extends TrackingEvent>(
    event: TrackingEvent,
    payload: TrackingEventPayload[T],
  ): Promise<void> {
    const data = {
      event,
      data: {
        ...(useSessionStore().tracking),
        is_app: __CAPACITOR__ && Capacitor.isNativePlatform(),
        ...payload,
      },
    }

    if (this.hasStarted === false) {
      this.queue.push(data)

      return
    }

    if (this.shouldTrack === false) {
      return
    }

    try {
      await this.http.post('tracking/event', data)
    } catch (error) {
      console.debug(error)
    }
  }

  private async sendQueue(): Promise<void> {
    const queueSnapshot = [...this.queue]
    this.queue.length = 0

    await Promise.all(
      queueSnapshot.map(item =>
        this.http.post('tracking/event', item).catch(err => console.debug(err))
      )
    )
  }

  private setStoreFromResponse(response: AxiosResponse): void {
    const trackingStore = useTrackingStore()

    trackingStore.setDeviceToken(response.headers['x-tracking-device-token'])
    trackingStore.setSessionToken(response.headers['x-tracking-session-token'])
  }
}